import React from "react";
import PropTypes from "prop-types";
import { ThemeProvider, Typography } from "@theconversation/ui";
import AddressDetails from "components/AddressDetails";
import donationsTheme from "components/donationsTheme";
import FormError from "components/FormError";
import FormFieldset from "components/FormFieldset";
import SubmitButton from "components/SubmitButton";
import ErrorTrack from "lib/ErrorTrack";
import i18n from "lib/i18n";
import { ConfigProps, ConfigDefaultProps } from "components/PropTypes/ConfigProps";

export default class AddressForm extends React.Component {
  static transformErrors(errors) {
    const transformedErrors = {};
    Object.keys(errors).forEach((key) => {
      const camelizedKey = key.replace(/(_\w)/, match => match.substr(1).toUpperCase());
      transformedErrors[camelizedKey] = new Array(errors[key]).join(", ");
    });
    return transformedErrors;
  }

  constructor(props) {
    super(props);

    this.state = {
      address: this.props.subscription.address || "",
      city: this.props.subscription.city || "",
      country: this.props.subscription.country || "",
      errors: AddressForm.transformErrors(this.props.subscription.errors || {}),
      postcode: this.props.subscription.postcode || "",
      state: this.props.subscription.state || "",
      submitting: false,
      complete: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onError = this.onError.bind(this);
    this.onLoad = this.onLoad.bind(this);
    this.setAddressDetails = this.setAddressDetails.bind(this);
  }

  onError(e) {
    this.setState({ submitting: false });
    ErrorTrack.notify(e);
    this.unclassifiedError(e);
  }

  onLoad(e) {
    this.setState({ submitting: false });
    try {
      const response = JSON.parse(e.target.responseText);
      if (response.success) {
        this.setState({ complete: true });
      } else {
        this.setState({ errors: AddressForm.transformErrors(response.errors) });
      }
    } catch (error) {
      ErrorTrack.notify(error);
      this.unclassifiedError(e);
    }
  }

  setAddressDetails(addressDetails) {
    this.setState(addressDetails);
  }

  unclassifiedError() {
    this.setState({ errors: { base: [i18n.t("capture_address.message.unrecoverable_error")] } });
  }

  handleSubmit(e) {
    e.preventDefault();
    this.setState({ errors: {} });
    const data = new FormData(this.form);
    this.setState({ submitting: true }, () => {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener("load", this.onLoad);
      xhr.addEventListener("error", this.onError);
      xhr.open("POST", this.props.url);
      xhr.send(data);
    });
  }

  renderForm() {
    if (this.state.complete) {
      return null;
    }

    return (
      <form
        acceptCharset="UTF-8"
        action={this.props.url}
        id="address-capture-form"
        method="post"
        onSubmit={this.handleSubmit}
        ref={(component) => { this.form = component; }}
      >
        <input name="utf8" type="hidden" value="✓" />
        <input name="authenticity_token" type="hidden" value={this.props.authenticityToken} />
        <input name="forms_capture_address[auth_token]" type="hidden" value={this.props.authToken} />
        <input
          name="forms_capture_address[subscription_id]"
          type="hidden"
          value={this.props.subscription.id}
        />
        <fieldset>
          <FormFieldset>
            <Typography variant="h6">{i18n.t("capture_address.title")}</Typography>
          </FormFieldset>
          {i18n.t("capture_address.blurb") !== "" &&
            <p>{i18n.t("capture_address.blurb")}</p>
          }
          <FormError error={this.state.errors.base} />

          <AddressDetails
            address={this.state.address}
            city={this.state.city}
            country={this.state.country}
            errors={this.state.errors}
            homeCountryCode={this.props.config.region_home_country_code}
            postcode={this.state.postcode}
            state={this.state.state}
            objectName="forms_capture_address"
            onChange={this.setAddressDetails}
          />
        </fieldset>

        <SubmitButton
          active={!this.state.submitting}
          text={i18n.t("capture_address.form.submit")}
        />
      </form>
    );
  }

  renderSuccessMessage() {
    if (this.state.complete) {
      return (
        <Typography variant="h6">{i18n.t("capture_address.success.thank_you_message")}</Typography>
      );
    }

    return null;
  }

  render() {
    return (
      <ThemeProvider theme={donationsTheme}>
        {this.renderForm()}
        {this.renderSuccessMessage()}
      </ThemeProvider>
    );
  }
}

AddressForm.defaultProps = {
  authenticityToken: "",
  authToken: "",
  config: ConfigDefaultProps,
  subscription: {
    errors: {},
  },
  url: "",
};

AddressForm.propTypes = {
  authenticityToken: PropTypes.string,
  authToken: PropTypes.string,
  config: ConfigProps,
  subscription: PropTypes.shape({
    address: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    errors: PropTypes.object,
    id: PropTypes.number,
    postcode: PropTypes.string,
    state: PropTypes.string,
  }),
  url: PropTypes.string,
};
